import { ComponentRef, Injectable, Type, ViewContainerRef } from '@angular/core';
import { AbstractControl, ValidationErrors, Validators } from '@angular/forms';
import _ from 'underscore';
import { IExtraFieldConfigurationObject } from './ga-formly.service';
import { util } from '../util';
import { IObject } from '../../../type_definitions/datatypes';

import { FormlyFieldConfig } from '@ngx-formly/core';
import { GaDeferredService, IGADeferred } from '../services/ga-deferred.service';
import { ComponentService } from '../services/component.service';
import { GaMessageService } from '@app/shared/message/ga-message.service';

export const URL_REGEXP = /^([a-z]{2,6}?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&\/=]*)$/i;
export const EMAIL_REGEXP = /^(([^<>()[\]\.,;:\s@\"]+(\.[^<>()[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/i;
export const DAY_REGEXP = /^([1-9]|[12]\d|3[01])$/;
export const AMOUNT_CURRENCY_REGEXP = /^\d*(\.\d{0,2})?$/;

export interface IValidatingComponent {
  beforeValidate?():Promise<boolean>|Promise<void|boolean>;
  validate(context?:IValidationContext):Promise<void|string[]>
}

export interface IValidatingComponentConfig {
  id?:string; // should match wizard step id
  class:Type<any>; // component class instance
}

export interface IComponentValidationResult extends IValidatingComponentConfig {
  errors:string[]; // list of errors generated by the component
}

export interface IValidationResults {
  allErrors:string[]; // a list containing concatenated errors from all components
  componentResults:IComponentValidationResult[]; // list of all components with their generated errors
}

export interface IValidationContext {
  type:string;
}

@Injectable()
export class GaValidateService {
  constructor(
    private deferredService:GaDeferredService,
    private componentService:ComponentService,
    private messageService:GaMessageService
  ) {
    /***/
  }

  // to debug validateComponent -> comment out the line componentRef.destroy() and make visible shadowValidationTemplate in wizard-controls.component.ts
  public validateComponents(components:IValidatingComponentConfig[], shadowValidationTemplate:ViewContainerRef, context?:any, inputs?:IObject):Promise<IValidationResults|void> {
    const componentService = this.componentService;

    async function performValidation():Promise<void | IValidationResults> {
      let componentRef:ComponentRef<any>;
      let componentInstance:any;

      let allErrors:string[] = [];
      const componentResults:IComponentValidationResult[] = [];
      for (const component of components) {
        componentRef = componentService.createComponent(shadowValidationTemplate, component.class, inputs);
        componentInstance = componentRef.instance;

        if (componentInstance && (!componentInstance.beforeValidate || !componentInstance.validate)) {
          console.warn(`beforeValidate() or validate() missing on step '${component.id}'`);
        }

        const beforeValidate:Promise<void> = componentInstance.beforeValidate? componentInstance.beforeValidate() : Promise.resolve();
        await beforeValidate
          .then(() => componentInstance.validate(context))
          .catch((componentErrors:string[]) => {
            if(componentErrors?.length) {
              allErrors = allErrors.concat(componentErrors);
              componentResults.push({
                class: component.class,
                id: component.id,
                errors: componentErrors
              });
            }
          })
          .finally(() => {
            // destroy the component
            componentRef.destroy();
          });
      }

      return allErrors.length ? Promise.reject({
        allErrors,
        componentResults
      }) : Promise.resolve();
    }

    return performValidation();
  }

  // useful when a formly form is spread over several child components (e.g. order entry location)
  public validateSubcomponents(childCompnentsFnToValidate:Promise<void|string[]>[]):Promise<void|string[]> {
    const deferred:IGADeferred<void|string[]> = this.deferredService.defer();

    Promise.allSettled(childCompnentsFnToValidate)
      .then(util.mergeRejected((errors:string[]) => {
        errors.length ? deferred.reject(errors) : deferred.resolve();
      }));

    return deferred.promise;
  }

  /**
   * @ngdoc method
   * @name SharedModule:GaValidateService#validateFields
   * @methodOf SharedModule:GaValidateService
   *
   * @description Validate formly fields based on the current model
   *
   * @param {IExtraFieldConfigurationObject} fields - a list of fields
   * @param {IObject} model - current model
   * @param {boolean} showError - show the error in formly fields
   * @param {string[]} prependExtraErrors - pass extra errors to the promise to add at the beginning of the list of errors (just to simplify the validation logic in the components)
   * @param {boolean} showErrorInDialog - show the errors in a dialog
   *
   * @return {Promise<void|string[]>} a list of errors
   */
  public validateFields = (fields:IExtraFieldConfigurationObject[], model:IObject, showError?:boolean, prependExtraErrors?:string[], showErrorInDialog?:boolean):Promise<void|string[]> => {
    const deferred: IGADeferred<string[] | void> = this.deferredService.defer();
    const promises = [];

    const checkFieldError = (field:IExtraFieldConfigurationObject, fieldModel?:IObject):void => {
      promises.push(
        this.validateField(field, (fieldModel || model))
      );

      if (showError && field.validation) {
        field.validation.show = true;
      }
    };

    const checkGroupFieldsError = (fields:IExtraFieldConfigurationObject[], fieldModel?:IObject):void => {
      for (const groupField of fields) {
        if(!groupField.hide) {
          checkFieldError(groupField, fieldModel);
        }
      }
    };

    for (const field of fields) {
      const hideExpression:Function = (<any>field).hideExpression;
      if (!field.hide && (!hideExpression || hideExpression && !hideExpression())){
        // check field error
        checkFieldError(field);

        // formly group
        if (field.data?.fields?.length) {
          checkGroupFieldsError(field.data.fields);
        }

        // formly array type validations
        if (field.fieldArray?.fieldGroup?.length) {
          for (const groupField of field.fieldGroup) {
            for (const childField of groupField.fieldGroup) {
              if (!childField.hide) {
                checkFieldError(childField, childField.model);
              }

              if (childField.data?.fields?.length) {
                checkGroupFieldsError(childField.data.fields, childField.model);
              }
            }
          }
        }
      }
    }

    let errors:string[] = [];
    Promise.all(promises)
      .then((fieldErrors:string[]) => {
        _.each(fieldErrors, (error:string) => {
          errors = errors.concat(error);
        });

        if(prependExtraErrors?.length){
          errors.push(...prependExtraErrors);
        }
        errors = _.uniq(errors);

        if(errors?.length){
          if(showErrorInDialog) {
            this.messageService.showErrorsDialog(errors);
          }
          deferred.reject(errors);
        } else {
          deferred.resolve();
        }
      });

    return deferred.promise;
  };

  public validateField(field:IExtraFieldConfigurationObject, model:any):string[]|Promise<string[]> {
    const errors:string[] = [];
    const basicValidations = [
      'required',
      'pattern',
      'minlength',
      'maxlength',
      'max',
      'min'
    ];

    const validateRequired:Function = field.type === 'checkbox' ? this.validateRequiredTrue : this.validateRequired;
    if (field.templateOptions.required && validateRequired(this.getValueFromField(field, model))) {
      errors.push(this.getErrorMessage(field, 'required'));
    }
    if (field.templateOptions.pattern && this.validatePattern(field.templateOptions.pattern, this.getValueFromField(field, model))) {
      errors.push(this.getErrorMessage(field, 'pattern'));
    }
    if (field.templateOptions.minLength && this.validateMinLength(field.templateOptions.minLength, this.getValueFromField(field, model))) {
      errors.push(this.getErrorMessage(field, 'minlength'));
    }
    if (field.templateOptions.maxLength && this.validateMaxLength(field.templateOptions.maxLength, this.getValueFromField(field, model))) {
      errors.push(this.getErrorMessage(field, 'maxlength'));
    }
    if (field.templateOptions.max && this.validateMax(field.templateOptions.max, this.getValueFromField(field, model))) {
      errors.push(this.getErrorMessage(field, 'max'));
    }
    if (_.isNumber(field.templateOptions.min) && this.validateMin(field.templateOptions.min, this.getValueFromField(field, model))) {
      errors.push(this.getErrorMessage(field, 'min'));
    }

    // check for custom validation apart from basic validation
    if (field.formControl?.invalid && field.formControl?.errors) {
      _.forEach(field.formControl.errors, (validator:IObject, validationKey:string) => {
        if (basicValidations.indexOf(validationKey) === -1) {
          // for async validation the message error is taken from validator.message
          const validationErrorMessage:string = <string>field.validation?.messages[ validationKey ] || validator.message;
          if(util.notNullOrUndefinedOrEmpty(validationErrorMessage) && !_.isFunction(validationErrorMessage)){
            errors.push(validationErrorMessage);
          }
          // validators set with custom expression and message, e.g.: multipleEmailValidator
          else if(_.isFunction(validationErrorMessage)) {
            const value:any = this.getValueFromField(field, model);
            const errorMessage:Function = () => validator.message(validationKey, {formControl: { value }});
            if(errorMessage()){
              errors.push(errorMessage());
            }
          }
        }
      });
    }

    // when using validateComponents() -> field.formControl is not available and custom validation won't trigger
    // this fixes the issue by re-evaluating custom validators/async validators using model value and collects error messages
    if(!field.formControl){
      // validators set with custom expression and message, e.g.: multipleEmailValidator
      if(field.validators){
        _.each(field.validators, (validator:FormlyFieldConfig['validators'], validationKey:string) => {
          const value:any = this.getValueFromField(field, model);
          if (_.isArray(validator)) {
            _.each(validator, (v: FormlyFieldConfig['validators'], vk:string) => {
              if (!_.isFunction(v)) {
                this.getValidatorErrorMessage(v, vk, value, errors);
              }
            });
          } else {
            if(!_.isFunction(validator)){
              this.getValidatorErrorMessage(validator, validationKey, value, errors);
            }
          }
        });
      }

      // asyncValidators validators set with custom expression and message, e.g.: uniqueUsername
      if(field.asyncValidators){
        return new Promise((resolve, reject) => {
          this.performAsyncValidations(field, model)
            .then((errorsAsyncValidators:string[]) => {
              resolve(errors.concat(errorsAsyncValidators));
            })
            .catch(reject);
        });
      }
    }

    return Promise.resolve(errors);
  }

  private getValidatorErrorMessage(validator:FormlyFieldConfig['validators'], validationKey:string, value:any, errors:string[]):void {
    const isValid:Function = () => validator?.expression({ value });
    const errorMessage:Function = () => validator.message(validationKey, {formControl: { value }});
    if(!isValid() && errorMessage()){
      errors.push(errorMessage());
    }
  }

  private performAsyncValidations(field:IExtraFieldConfigurationObject, model:any):Promise<string[]>{
    const errors:string[] = [];
    return new Promise((resolve) => {
      const asyncValidatorsPromises:Promise<any>[] = [];
      const asyncValidatorsMessages:string[] = [];
      _.each(field.asyncValidators, (asyncValidator:FormlyFieldConfig['asyncValidators']) => {
        asyncValidatorsPromises.push(
          asyncValidator.expression({ value: this.getValueFromField(field, model) }).toPromise()
        );
        asyncValidatorsMessages.push(asyncValidator.message);
      });

      // revalidate async validators
      return Promise.all(asyncValidatorsPromises)
        .then((results:any[]) => {
          _.each(results, (valid:any, idx:number) => {
            if (!valid && asyncValidatorsMessages[idx]) {
              errors.push(asyncValidatorsMessages[idx]);
            }
          });
          resolve(errors);
        });
    });
  }

  private getValueFromField(field:IExtraFieldConfigurationObject, model:any):any {
    const value:any = model && !_.isEmpty(model) ? model[<string>field.key] : undefined;
    return value || util.getValue(model, field.key); // get value from a field having the key like: model[properties.property.value]
  }

  private validateRequired(value:any):boolean {
    const validationErrors:ValidationErrors = Validators.required(<AbstractControl>{ value });
    return validationErrors?.required;
  }

  private validateRequiredTrue(value:any):boolean {
    const validationErrors:ValidationErrors = Validators.requiredTrue(<AbstractControl>{ value });
    return validationErrors?.required;
  }

  private validatePattern(pattern:string | RegExp, value:any):boolean {
    const validationErrors:ValidationErrors = Validators.pattern(pattern)(<AbstractControl>{ value });
    return validationErrors?.pattern;
  }

  private validateMinLength(minLength:number, value:any):boolean {
    const validationErrors:ValidationErrors = Validators.minLength(minLength)(<AbstractControl>{ value });
    return validationErrors?.minlength;
  }

  private validateMaxLength(maxLength:number, value:any):boolean {
    const validationErrors:ValidationErrors = Validators.maxLength(maxLength)(<AbstractControl>{ value });
    return validationErrors?.maxlength;
  }

  private validateMax(max:number, value:any):boolean {
    const validationErrors:ValidationErrors = Validators.max(max)(<AbstractControl>{ value });
    return validationErrors?.max;
  }

  private validateMin(min:number, value:any):boolean {
    const validationErrors:ValidationErrors = Validators.min(min)(<AbstractControl>{ value });
    return validationErrors?.min;
  }

  private getErrorMessage(field:IExtraFieldConfigurationObject, type:string):string {
    const message:string | Function = field.validation.messages[ type ];
    return _.isString(message)? message : (<Function>message)(undefined, field);
  }

}